import { cn } from "@newxsystems/shared";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import { TextEffect } from "../../../../text-effects/TextEffect.client";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { ICallToActionSplitProps } from "./CallToActionSplit.props";

// The image column carries the proportion rather than a width on the image, so
// the copy column absorbs what the image does not use. A small image leaves the
// copy most of the row instead of both tracks sitting at a fixed half with the
// difference stranded between them.
const columns = {
  small: "md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]",
  medium: "md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]",
  large: "md:grid-cols-2",
} as const;

export const CallToActionSplit = ({
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  image,
  imageWidth,
}: ICallToActionSplitProps) => (
  <div
    className={cn(
      "relative z-10 grid grid-cols-1 items-end gap-8 md:gap-14",
      image ? columns[imageWidth || "medium"] : "md:grid-cols-2",
    )}
    data-reveal="stack"
  >
    {eyebrow ? (
      <p className="signal-label order-1 text-primary-foreground md:col-start-1">{eyebrow}</p>
    ) : null}
    {image ? (
      <DaylightMedia
        aspectRatio={image.aspectRatio || "16:9"}
        className="order-5 w-full justify-self-end ring-primary-foreground/25 md:order-none md:col-start-2 md:row-span-4 md:row-start-1"
        imageClassName="object-cover"
        media={image.image}
        effect={image.effect}
        sizes="(min-width: 768px) 50vw, 100vw"
      />
    ) : null}
    {heading ? (
      <h2
        className="order-2 max-w-[13ch] break-words font-daylight-display text-[clamp(2.25rem,5.2vw,3.5rem)] font-semibold leading-[0.98] tracking-[-0.035em] md:col-start-1"
        data-reveal-enter="wipe"
      >
        <TextEffect effect={headingEffect} text={heading} />
      </h2>
    ) : null}
    {description ? (
      <DaylightRichText
        className="order-3 max-w-[52ch] text-primary-foreground/80 md:col-start-1 [&_a]:text-primary-foreground [&_strong]:text-primary-foreground"
        content={description}
      />
    ) : null}
    {buttons?.length ? (
      <div
        className={cn(
          "order-4 flex flex-wrap gap-3",
          image
            ? "md:col-start-1"
            : "md:col-start-2 md:row-span-3 md:row-start-1 md:justify-self-end",
        )}
      >
        {buttons.map((button, index) => (
          <DaylightAction action={button} key={`${button.href}-${index}`} tone="onAccent" />
        ))}
      </div>
    ) : null}
  </div>
);

CallToActionSplit.displayName = "Content::CallToActionSplit";
